import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ExamPaperLayout from '../../../components/Exam/ExamPaperLayout';
import ExamHoerenContent from '../../../components/Exam/ExamHoerenContent';
import ExamResultsPage from '../../../components/Exam/ExamResultsPage';
import { examB2API } from '../../../services/api';
import { HOEREN_TASKS as FALLBACK_TASKS } from '../../../data/exam/b2';

const HoerenPage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('exam');
  const [activeTaskIdx, setActiveTaskIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [resultDetails, setResultDetails] = useState({});
  const [scoreData, setScoreData] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    examB2API.getTasks('hoeren')
      .then(({ tasks: t }) => setTasks(t))
      .catch(() => setTasks(FALLBACK_TASKS))
      .finally(() => setLoading(false));
  }, []);

  const task = tasks[activeTaskIdx];
  const totalTeile = tasks.length || 0;
  const isFirstTeil = activeTaskIdx === 0;
  const isLastTeil = activeTaskIdx === totalTeile - 1;

  const questionOffset = useMemo(
    () => tasks.slice(0, activeTaskIdx).reduce((n, t) => n + (t.questions?.length || 0), 0),
    [tasks, activeTaskIdx],
  );

  const setAnswer = (qId, value) => {
    if (submitted) return;
    const normalized = typeof value === 'string' && value.length === 1 ? value.toLowerCase() : value;
    setAnswers((prev) => ({ ...prev, [qId]: normalized }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await examB2API.submitHoeren(answers);
      const data = res.data.data;
      const detailsMap = {};
      (data.details || []).forEach((d) => { detailsMap[d.question_id] = d; });
      setResultDetails(detailsMap);
      setScoreData(data);
      setSubmitted(true);
      setView('results');
    } catch {
      setSubmitted(true);
      setScoreData({ correct: 0, total: 0, score: 0, passed: false });
      setView('results');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setAnswers({});
    setSubmitted(false);
    setResultDetails({});
    setScoreData(null);
    setActiveTaskIdx(0);
    setShowTranscript(false);
    setView('exam');
  };

  if (loading) {
    return (
      <div className="exam-paper-root flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-zinc-400" />
      </div>
    );
  }

  if (view === 'results' && scoreData) {
    return (
      <ExamResultsPage
        skillName="Hören"
        scoreData={scoreData}
        tasks={tasks}
        answers={answers}
        resultDetails={resultDetails}
        onRetry={handleRetry}
        onBack={() => navigate('/exam/b2')}
        onReviewPaper={() => setView('review')}
      />
    );
  }

  if (view === 'review' && submitted) {
    return (
      <ExamPaperLayout
        moduleKey="hoeren"
        moduleName="Hören"
        moduleDuration="circa 40 Minuten"
        onExit={() => setView('results')}
      >
        <p className="exam-instruction font-bold mb-4 bg-brand-primary/10 border border-brand-primary/30 p-2">
          Prüfungsblatt mit Auswertung — grün = richtig, rot = falsch
        </p>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-sans font-bold text-brand-text/70">
            Teil {activeTaskIdx + 1} von {totalTeile}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="exam-btn-secondary"
              onClick={() => setActiveTaskIdx((i) => Math.max(0, i - 1))}
              disabled={isFirstTeil}
            >
              ← Zurück
            </button>
            <button
              type="button"
              className="exam-btn-secondary"
              onClick={() => setActiveTaskIdx((i) => Math.min(totalTeile - 1, i + 1))}
              disabled={isLastTeil}
            >
              Weiter →
            </button>
          </div>
        </div>
        {task && (
          <ExamHoerenContent
            task={task}
            answers={answers}
            submitted
            resultDetails={resultDetails}
            onAnswer={() => {}}
            questionStartIndex={questionOffset}
            showTranscript={showTranscript}
            onToggleTranscript={() => setShowTranscript((v) => !v)}
          />
        )}
        <div className="exam-submit-bar">
          <button type="button" className="exam-btn-primary" onClick={() => setView('results')}>
            Zur Auswertung
          </button>
        </div>
      </ExamPaperLayout>
    );
  }

  if (!task) return null;

  return (
    <ExamPaperLayout
      moduleKey="hoeren"
      moduleName="Hören"
      moduleDuration="circa 40 Minuten"
      totalMinutes={40}
      timerRunning={!submitted}
      onExit={() => navigate('/exam/b2')}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-sans font-bold text-brand-text/70">
          Teil {activeTaskIdx + 1} von {totalTeile}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            className="exam-btn-secondary"
            onClick={() => setActiveTaskIdx((i) => Math.max(0, i - 1))}
            disabled={isFirstTeil}
          >
            ← Zurück
          </button>
          <button
            type="button"
            className="exam-btn-secondary"
            onClick={() => setActiveTaskIdx((i) => Math.min(totalTeile - 1, i + 1))}
            disabled={isLastTeil}
          >
            Weiter →
          </button>
        </div>
      </div>

      <ExamHoerenContent
        task={task}
        answers={answers}
        submitted={submitted}
        resultDetails={resultDetails}
        onAnswer={setAnswer}
        questionStartIndex={questionOffset}
        showTranscript={showTranscript}
        onToggleTranscript={() => setShowTranscript((v) => !v)}
      />

      <div className="exam-submit-bar">
        {!submitted ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-sans text-brand-text/60">
              Tipp: Mit „Weiter“ kannst du die Teile durchgehen. Am Ende „Prüfung abgeben“.
            </p>
            <button
              type="button"
              className="exam-btn-primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Wird ausgewertet…' : 'Prüfung abgeben und auswerten'}
            </button>
          </div>
        ) : (
          <button type="button" className="exam-btn-primary" onClick={() => setView('results')}>
            Auswertung anzeigen
          </button>
        )}
      </div>
    </ExamPaperLayout>
  );
};

export default HoerenPage;
